import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import React from "react";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
    ComparisonValidityState,
    IComparisonInfo,
} from "../declarations/types";
import { useTheme } from "@mui/material/styles";

interface IComparisonTableProps {
    data: IComparisonInfo[] | undefined;
}

function SourceChip({ label, tooltip, isDark }: { label: "CS" | "D365"; tooltip?: string; isDark?: boolean }) {
    return (
        <Tooltip title={tooltip ?? `Value from ${label}`} arrow>
            <Chip
                label={label}
                size="small"
                sx={{
                    height: 18,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    backgroundColor: label === "CS"
                        ? (isDark ? "rgba(21, 101, 192, 0.2)" : "#e3f2fd")
                        : (isDark ? "rgba(198, 40, 40, 0.2)" : "#fce4ec"),
                    color: label === "CS"
                        ? (isDark ? "#90caf9" : "#1565c0")
                        : (isDark ? "#ef9a9a" : "#c62828"),
                    cursor: "default",
                }}
            />
        </Tooltip>
    );
}

function ValueCell({ row, isDark }: { row: IComparisonInfo; isDark?: boolean }) {
    const csVal = row.sourceValue ?? "";
    const d365Val = row.targetValue ?? "";

    if (row.validity === ComparisonValidityState.Valid) {
        return <Typography variant="body2">{csVal || d365Val || "—"}</Typography>;
    }

    if (row.validity === ComparisonValidityState.Warning) {
        const hasCS = csVal !== "" && csVal !== null;
        const hasD365 = d365Val !== "" && d365Val !== null;
        const value = hasCS ? csVal : d365Val;
        const source: "CS" | "D365" = hasCS ? "CS" : "D365";
        const missing = hasCS ? "D365" : "CS";

        return (
            <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="body2">{String(value) || "—"}</Typography>
                <SourceChip label={source} tooltip={`Value from ${source} — missing in ${missing}`} isDark={isDark} />
            </Stack>
        );
    }

    // Invalid — show both values
    return (
        <Stack spacing={0.25}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
                <SourceChip label="CS" tooltip={`CS value: ${String(csVal) || "—"}`} isDark={isDark} />
                <Typography variant="body2">{String(csVal) || "—"}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.75}>
                <SourceChip label="D365" tooltip={`D365 value: ${String(d365Val) || "—"}`} isDark={isDark} />
                <Typography variant="body2">{String(d365Val) || "—"}</Typography>
            </Stack>
        </Stack>
    );
}

export default function ComparisonTable(props: IComparisonTableProps) {
    const theme = useTheme();

    if (!props.data || props.data.length === 0) {
        return null;
    }

    const isDark = theme.palette.mode === "dark";

    function getRowBackground(validity: ComparisonValidityState): string | undefined {
        switch (validity) {
            case ComparisonValidityState.Valid:
                return isDark ? "rgba(76, 175, 80, 0.1)" : "rgba(76, 175, 80, 0.15)";
            case ComparisonValidityState.Invalid:
                return isDark ? "rgba(244, 67, 54, 0.15)" : "rgba(244, 67, 54, 0.12)";
            case ComparisonValidityState.Warning:
                return isDark ? "rgba(255, 152, 0, 0.12)" : "rgba(255, 152, 0, 0.15)";
        }
    }

    // Use the source property name, falling back to target
    function getPropertyName(row: IComparisonInfo): string {
        return row.sourcePropertyName || row.targetPropertyName || "—";
    }

    return (
        <TableContainer component={Paper} sx={{ tableLayout: "fixed" }}>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.secondary.contrastText,
                        }}
                    >
                        <TableCell sx={{ fontWeight: 600, width: "40%" }}>Property</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: "60%" }}>Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[...props.data].sort((a, b) => {
                        // Invalid first, then Warning, then Valid
                        const order = { [ComparisonValidityState.Invalid]: 0, [ComparisonValidityState.Warning]: 1, [ComparisonValidityState.Valid]: 2 };
                        return order[a.validity] - order[b.validity];
                    }).map((row, index) => (
                        <TableRow
                            key={index}
                            sx={{ backgroundColor: getRowBackground(row.validity) }}
                        >
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2" fontWeight={500}>
                                    {getPropertyName(row)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ wordBreak: "break-all" }}>
                                <ValueCell row={row} isDark={isDark} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
